import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import {
    getPlansRevisionsActiveGetV2QueryKey,
    plansRevisionsActiveGetV2,
} from "handlers/plans/plans";

/** Tooltip shown wherever a set is blocked from a new revision because one is already pending review. */
export const PENDING_REVISION_TOOLTIP =
    "This set has a revision waiting to be reviewed. Review or discard it before uploading another.";

/**
 * Returns the set ids that currently have a pending (unfinalized) revision import. A set with a pending
 * draft can't accept another revision until that draft is reviewed or discarded, so consumers use this
 * to disable those sets (e.g. the upload modal's per-file "Select set" dropdown, or the plan set's
 * "Upload revision" more-menu item).
 *
 * Shares its query key with PlanRevisionsBanner, so results are shared from the cache rather than
 * refetched. Queries only run while `enabled` is true.
 */
export function usePendingRevisionSetIds(
    jobId: number,
    setIds: string[],
    enabled: boolean,
): Set<string> {
    const queries = useQueries({
        queries: setIds.map((setId) => ({
            queryKey: getPlansRevisionsActiveGetV2QueryKey({ setId, jobId }),
            queryFn: () => plansRevisionsActiveGetV2({ setId, jobId }),
            enabled: enabled && !!setId && !!jobId,
        })),
    });

    return useMemo(() => {
        const pending = new Set<string>();
        queries.forEach((query, index) => {
            if (query.data?.data.draftId) {
                pending.add(setIds[index]);
            }
        });
        return pending;
    }, [queries, setIds]);
}
